class GaugeProgressBarController {
    constructor() {
        "ngInject";
    }

    $onInit() {
        // Set defaults - handle @ bindings (strings) and < bindings (values)
        // @ bindings come as strings, so parse them
        this.size = (this.size && !isNaN(parseInt(this.size))) ? parseInt(this.size) : 200;
        this.strokeWidth = (this.strokeWidth && !isNaN(parseInt(this.strokeWidth))) ? parseInt(this.strokeWidth) : 20;
        this.color = this.color || '#27ae60';
        this.bgColor = this.bgColor || '#e0e0e0';
        
        // Handle min/max values
        if (this.minValue !== undefined && this.minValue !== null) {
            this.minValue = parseFloat(this.minValue);
        } else {
            this.minValue = 0;
        }
        
        if (this.maxValue !== undefined && this.maxValue !== null) {
            this.maxValue = parseFloat(this.maxValue);
        } else if (this.total !== undefined && this.total !== null) {
            this.maxValue = parseFloat(this.total);
        } else {
            this.maxValue = 100;
        }
        
        this.showPercentage = this.showPercentage !== false;
        this.showValue = this.showValue !== false;
        this.showLabel = this.showLabel === true;
        this.label = this.label || '';
        this.gaugeType = (this.gaugeType || 'semicircle').toLowerCase();

        // Generate unique ID for clipPath
        this.clipPathId = 'gauge-clip-' + Math.random().toString(36).substr(2, 9);

        // Calculate radius (size / 2 - strokeWidth/2 to account for stroke)
        this.radius = (this.size / 2) - (this.strokeWidth / 2);
        this.center = this.size / 2;

        // For semicircle gauge, we use 180 degrees (π radians)
        // For full circle, we use 360 degrees (2π radians)
        this.startAngle = this.gaugeType === 'circle' ? -90 : 180;
        this.angleRange = this.gaugeType === 'circle' ? 360 : 180;
    }

    getProgressPercentage() {
        const current = parseFloat(this.current) || 0;
        const max = parseFloat(this.maxValue) || 100;
        const min = parseFloat(this.minValue) || 0;
        const range = max - min;
        
        if (range === 0) return 0;
        
        const normalizedValue = Math.max(min, Math.min(max, current)) - min;
        return Math.min(100, (normalizedValue / range) * 100);
    }

    getCircumference() {
        // Always use full circle circumference for stroke-dasharray
        // The clipping will handle showing only the semicircle portion
        return 2 * Math.PI * this.radius;
    }

    getStrokeDashoffset() {
        const percentage = this.getProgressPercentage();
        const circumference = this.getCircumference();
        
        if (this.gaugeType === 'circle') {
            // For full circle: calculate offset normally
            // Start from top (-90° rotation), fill clockwise
            return circumference - (percentage / 100 * circumference);
        } else {
            // For semicircle gauge:
            // We rotate by 180° so the circle is flipped
            // After 180° rotation: 0° (right) becomes left, 180° (left) becomes right
            // We clip to show the bottom half (y from center to size)
            // The bottom semicircle goes from left to right along the bottom arc
            // Progress should fill from left to right
            const semicircleLength = Math.PI * this.radius;
            const visibleLength = (percentage / 100) * semicircleLength;
            
            // With 180° rotation:
            // The circle is flipped, so what was top is now bottom
            // The bottom half (after clipping) shows the arc from left to right
            // We want to show progress filling from left to right
            // At 0%: show nothing (offset = circumference)
            // At 100%: show full semicircle (offset = circumference - semicircleLength)
            // But we need to account for the rotation - after 180° rotation, the start point is at the left
            // So we offset to hide the top half and then show the visible portion from left to right
            return circumference - visibleLength;
        }
    }

    getCurrentValue() {
        return parseFloat(this.current) || 0;
    }

    getMaxValue() {
        return parseFloat(this.maxValue) || parseFloat(this.total) || 100;
    }

    getMinValue() {
        return parseFloat(this.minValue) || 0;
    }

    // Get the viewBox for the SVG
    getViewBox() {
        if (this.gaugeType === 'circle') {
            return `0 0 ${this.size} ${this.size}`;
        } else {
            // For semicircle, show the full circle but we'll only draw the bottom arc
            // Use full size viewBox, but adjust the SVG height to show only bottom half
            return `0 0 ${this.size} ${this.size}`;
        }
    }

    // Get the SVG height for the container
    getSvgHeight() {
        if (this.gaugeType === 'circle') {
            return this.size;
        } else {
            // For semicircle, show only the bottom half plus padding
            // The circle center is at (size/2, size/2), so bottom half starts at center
            return Math.ceil(this.size / 2) + 10;
        }
    }

    // Get SVG styles for positioning
    getSvgStyles() {
        if (this.gaugeType === 'semicircle') {
            return {
                position: 'absolute',
                bottom: '0',
                left: '0'
            };
        }
        return {};
    }

    // Get content overlay styles
    getContentStyles() {
        const baseStyles = {
            position: 'absolute',
            left: '50%',
            textAlign: 'center',
            pointerEvents: 'none',
            zIndex: 1,
            width: '100%'
        };

        if (this.gaugeType === 'circle') {
            // For full circle, center it in the middle
            baseStyles.top = '50%';
            baseStyles.transform = 'translate(-50%, -50%)';
        } else {
            // For semicircle:
            // Center the content in the middle of the visible semicircle area
            // The container is (size/2 + 10) tall, and the SVG is at the bottom
            // The visible semicircle is roughly centered in the container
            baseStyles.top = '50%';
            baseStyles.transform = 'translate(-50%, -50%)';
        }

        return baseStyles;
    }

    // Get the semicircle path (arc from left to right, showing bottom half)
    getSemicirclePath() {
        const radius = this.radius;
        const center = this.center;
        
        // For a semicircle gauge:
        // We want an arc that goes from left (180°) to right (0°)
        // In SVG coordinates: 0° is right, 90° is down, 180° is left, 270° is up
        // Circle center is at (center, center)
        // Start at left side: (center - radius, center) at 180°
        // End at right side: (center + radius, center) at 0°
        // To go through the bottom (90°), we use sweep=1 (clockwise)
        const startX = center - radius;
        const startY = center;
        const endX = center + radius;
        const endY = center;
        
        // Arc parameters: rx, ry, x-axis-rotation, large-arc-flag, sweep-flag
        // For 180° arc: large-arc-flag doesn't matter (both arcs are 180°)
        // sweep-flag = 1 means clockwise (goes through bottom at 90°)
        return `M ${startX} ${startY} A ${radius} ${radius} 0 0 1 ${endX} ${endY}`;
    }
}

export default GaugeProgressBarController;




// WEBPACK FOOTER //
// ./client/common/directives/gauge-progress-bar/gauge-progress-bar.controller.js